<template>
  <div class="page-card mall-page">
    <div class="page-head">
      <h2 class="page-title">商品管理</h2>
      <el-button type="primary" icon="el-icon-refresh" @click="refreshAll">刷新</el-button>
    </div>

    <el-tabs v-model="activeTab">
      <el-tab-pane label="商品管理" name="products">
        <div class="toolbar">
          <div class="toolbar-left">
            <el-input v-model="query.keyword" clearable placeholder="商品标题/角标" style="width: 220px" />
            <el-select v-model="query.status" clearable placeholder="状态" style="width: 130px">
              <el-option v-for="item in statusOptions" :key="item.value" :label="item.label" :value="item.value" />
            </el-select>
            <el-select v-model="query.productType" clearable placeholder="商品类型" style="width: 140px">
              <el-option v-for="item in productTypeOptions" :key="item.value" :label="item.label" :value="item.value" />
            </el-select>
            <el-select v-model="query.publishSource" clearable placeholder="发布来源" style="width: 140px">
              <el-option v-for="item in publishSourceOptions" :key="item.value" :label="item.label" :value="item.value" />
            </el-select>
            <el-cascader
              v-model="query.categoryId"
              :options="enabledCategoryOptions"
              :props="categoryCascaderProps"
              placeholder="商品分类"
              style="width: 200px"
              clearable
              filterable
            />
            <el-button type="primary" @click="fetchProducts">查询</el-button>
          </div>
          <el-button v-if="canCreate" type="primary" icon="el-icon-plus" @click="openProductDialog()">新增商品</el-button>
        </div>

        <el-table :data="products" border>
          <el-table-column label="商品" min-width="240">
            <template slot-scope="{ row }">
              <div class="product-cell">
                <img v-if="productCoverUrl(row)" class="product-cover" :src="fullUrl(productCoverUrl(row))" alt="">
                <div v-else class="product-cover product-cover-empty">封面</div>
                <div class="product-info">
                  <div class="product-title">{{ row.title || '-' }}</div>
                  <div class="product-subtitle">{{ row.subtitle || '-' }}</div>
                </div>
              </div>
            </template>
          </el-table-column>
          <el-table-column label="类型" width="110">
            <template slot-scope="{ row }">{{ productTypeLabel(row.productType) }}</template>
          </el-table-column>
          <el-table-column label="分类" min-width="150">
            <template slot-scope="{ row }">
              <div>{{ categoryLabel(row) }}</div>
            </template>
          </el-table-column>
          <el-table-column label="来源" width="160">
            <template slot-scope="{ row }">
              <div>{{ publishSourceLabel(row.publishSource) }}</div>
              <div v-if="row.merchantName || row.merchantBrandName" class="muted-text">
                {{ row.merchantBrandName || row.merchantName }}
              </div>
            </template>
          </el-table-column>
          <el-table-column prop="price" label="价格" width="100" />
          <el-table-column label="运费" width="110">
            <template slot-scope="{ row }">{{ freightLabel(row) }}</template>
          </el-table-column>
          <el-table-column prop="badgeText" label="角标" width="110" />
          <el-table-column label="状态" width="100">
            <template slot-scope="{ row }">
              <el-tag :type="statusTagType(row.status)" size="small">{{ statusLabel(row.status) }}</el-tag>
            </template>
          </el-table-column>
          <el-table-column prop="publishedAt" label="发布时间" width="170" />
          <el-table-column prop="updatedAt" label="更新时间" width="170" />
          <el-table-column label="操作" width="190" fixed="right">
            <template slot-scope="{ row }">
              <el-button v-if="canUpdate" type="text" @click="openProductDialog(row)">编辑</el-button>
              <el-button v-if="row.status !== 2 && canPublish" type="text" @click="publishProduct(row)">上架</el-button>
              <el-button v-if="row.status === 2 && canOffline" type="text" class="danger-link" @click="offlineProduct(row)">下架</el-button>
            </template>
          </el-table-column>
        </el-table>
      </el-tab-pane>

      <el-tab-pane label="商品分类管理" name="categories">
        <div class="toolbar">
          <div class="toolbar-left">
            <el-input v-model="categoryQuery.keyword" clearable placeholder="分类名称" style="width: 220px" />
            <el-button type="primary" @click="fetchCategories">查询</el-button>
          </div>
          <el-button v-if="canCategory" type="primary" icon="el-icon-plus" @click="openCategoryDialog()">新增一级分类</el-button>
        </div>

        <el-table
          :data="categoryTree"
          row-key="id"
          border
          default-expand-all
          :tree-props="{ children: 'children' }"
        >
          <el-table-column prop="categoryName" label="分类名称" min-width="220" />
          <el-table-column label="级别" width="100">
            <template slot-scope="{ row }">{{ row.level === 1 ? '一级' : '二级' }}</template>
          </el-table-column>
          <el-table-column prop="sortNo" label="排序" width="90" />
          <el-table-column label="状态" width="100">
            <template slot-scope="{ row }">
              <el-tag :type="row.status === 1 ? 'success' : 'info'" size="small">
                {{ row.status === 1 ? '启用' : '停用' }}
              </el-tag>
            </template>
          </el-table-column>
          <el-table-column prop="updatedAt" label="更新时间" width="170" />
          <el-table-column label="操作" width="240" fixed="right">
            <template slot-scope="{ row }">
              <el-button
                v-if="row.level === 1 && canCategory"
                type="text"
                @click="openCategoryDialog(null, row)"
              >
                新增子分类
              </el-button>
              <el-button v-if="canCategory" type="text" @click="openCategoryDialog(row)">编辑</el-button>
              <el-button v-if="canCategory" type="text" class="danger-link" @click="removeCategory(row)">删除</el-button>
            </template>
          </el-table-column>
        </el-table>
      </el-tab-pane>

      <el-tab-pane label="推荐配置" name="recommendation">
        <el-card shadow="never" class="section-card">
          <div slot="header" class="section-header">
            <span>艺人商城推荐商品</span>
            <el-button v-if="canRecommend" type="primary" size="small" @click="submitRecommendation">保存配置</el-button>
          </div>

          <el-form label-width="120px">
            <el-form-item label="推荐方式">
              <el-radio-group v-model="recommendForm.recommendMode">
                <el-radio :label="1">人工推荐</el-radio>
                <el-radio :label="2">系统自动推荐</el-radio>
              </el-radio-group>
            </el-form-item>
            <el-form-item v-if="recommendForm.recommendMode === 1" label="推荐商品">
              <el-select
                v-model="recommendForm.productIds"
                multiple
                filterable
                remote
                reserve-keyword
                placeholder="搜索已上架商品"
                :remote-method="fetchRecommendProductOptions"
                :loading="recommendLoading"
                style="width: 100%"
              >
                <el-option
                  v-for="item in recommendOptions"
                  :key="item.id"
                  :label="item.subtitle ? `${item.title} (${item.subtitle})` : item.title"
                  :value="String(item.id)"
                />
              </el-select>
              <div class="form-tip">选择顺序即前台展示顺序；已下架商品保存时会自动过滤。</div>
            </el-form-item>
            <el-form-item v-else label="排序规则">
              <el-alert title="系统自动推荐按发布时间倒序展示：publishedAt DESC, id DESC。" type="info" :closable="false" />
            </el-form-item>
          </el-form>

          <el-table :data="recommendProducts" border>
            <el-table-column label="当前人工推荐商品" min-width="240">
              <template slot-scope="{ row }">
                <div class="product-title">{{ row.title || '-' }}</div>
                <div class="muted-text">{{ row.subtitle || '-' }}</div>
              </template>
            </el-table-column>
            <el-table-column prop="price" label="价格" width="110" />
            <el-table-column label="状态" width="100">
              <template slot-scope="{ row }">
                <el-tag :type="statusTagType(row.status)" size="small">{{ statusLabel(row.status) }}</el-tag>
              </template>
            </el-table-column>
            <el-table-column prop="publishedAt" label="发布时间" width="170" />
          </el-table>
        </el-card>
      </el-tab-pane>
    </el-tabs>

    <el-dialog :title="productForm.id ? '编辑商品' : '新增商品'" :visible.sync="productDialogVisible" width="760px">
      <el-form ref="productFormRef" :model="productForm" :rules="productRules" label-width="112px">
        <el-form-item label="商品类型" prop="productType">
          <el-radio-group v-model="productForm.productType">
            <el-radio :label="1">虚拟服务</el-radio>
            <el-radio :label="2">实物商品</el-radio>
          </el-radio-group>
        </el-form-item>
        <el-form-item label="发布来源" prop="publishSource">
          <el-radio-group v-model="productForm.publishSource" @change="handlePublishSourceChange">
            <el-radio :label="1">平台自营</el-radio>
            <el-radio :label="2">商家代发</el-radio>
          </el-radio-group>
        </el-form-item>
        <el-form-item v-if="productForm.publishSource === 2" label="归属商家" prop="merchantUserId">
          <el-select
            v-model="productForm.merchantUserId"
            filterable
            remote
            reserve-keyword
            placeholder="搜索商家"
            :remote-method="fetchMerchantOptions"
            :loading="merchantLoading"
            style="width: 100%"
          >
            <el-option
              v-for="item in merchantOptions"
              :key="item.id"
              :label="item.subtitle ? `${item.name} (${item.subtitle})` : item.name"
              :value="item.id"
            />
          </el-select>
        </el-form-item>
        <el-form-item label="商品分类" prop="categoryId">
          <el-cascader
            v-model="productForm.categoryId"
            :options="enabledCategoryOptions"
            :props="categoryCascaderProps"
            :show-all-levels="true"
            placeholder="请选择二级分类"
            style="width: 100%"
            clearable
            filterable
          />
          <div v-if="!enabledCategoryOptions.length" class="form-tip">请先在「商品分类管理」中创建并启用二级分类。</div>
        </el-form-item>
        <el-form-item label="商品标题" prop="title">
          <el-input v-model="productForm.title" maxlength="120" show-word-limit />
        </el-form-item>
        <el-form-item label="副标题">
          <el-input v-model="productForm.subtitle" maxlength="255" show-word-limit />
        </el-form-item>
        <el-form-item label="商品素材">
          <div class="media-upload-actions">
            <el-upload
              action="#"
              :show-file-list="false"
              :http-request="(option) => uploadProductMedia(option, 'image')"
              :accept="imageAccept"
            >
              <el-button icon="el-icon-picture-outline">上传图片</el-button>
            </el-upload>
            <el-upload
              action="#"
              :show-file-list="false"
              :http-request="(option) => uploadProductMedia(option, 'video')"
              :accept="videoAccept"
            >
              <el-button icon="el-icon-video-camera">上传视频</el-button>
            </el-upload>
            <span class="form-tip">支持图片和视频；未单独设置封面时，第一张图片自动作为封面。</span>
          </div>
          <div v-if="productMediaList.length" class="product-media-grid">
            <div
              v-for="(item, index) in productMediaList"
              :key="`${item.type}-${item.url}-${index}`"
              class="product-media-card"
              :class="{ 'product-media-card-cover': item.type === 'image' && item.url === productForm.coverUrl }"
            >
              <img v-if="item.type === 'image'" :src="fullUrl(item.url)" alt="">
              <video v-else class="product-media-video" :src="fullUrl(item.url)" controls preload="metadata">
                当前浏览器不支持视频预览
              </video>
              <div class="media-type-badge">{{ item.type === 'image' ? '图片' : '视频' }}</div>
              <div v-if="item.type === 'image' && item.url === productForm.coverUrl" class="cover-badge">封面</div>
              <div class="product-media-actions">
                <el-button
                  v-if="item.type === 'image' && item.url !== productForm.coverUrl"
                  type="text"
                  size="mini"
                  @click="setCoverMedia(item)"
                >
                  设为封面
                </el-button>
                <el-button type="text" size="mini" class="danger-link" @click="removeProductMedia(index)">删除</el-button>
              </div>
            </div>
          </div>
          <div v-else class="form-tip">还没有素材，上传第一张图片后会自动作为商品封面。</div>
        </el-form-item>
        <el-form-item label="售价" prop="price">
          <el-input-number v-model="productForm.price" :min="0" :precision="2" style="width: 220px" />
        </el-form-item>
        <el-form-item label="划线价">
          <el-input-number v-model="productForm.originalPrice" :min="0" :precision="2" style="width: 220px" />
        </el-form-item>
        <el-form-item v-if="productForm.productType === 2" label="运费设置">
          <div class="freight-editor">
            <el-radio-group v-model="productForm.freightType">
              <el-radio :label="1">包邮</el-radio>
              <el-radio :label="2">固定运费</el-radio>
            </el-radio-group>
            <el-input-number
              v-if="productForm.freightType === 2"
              v-model="productForm.freightAmount"
              :min="0"
              :precision="2"
              placeholder="运费金额"
              style="width: 180px"
            />
          </div>
        </el-form-item>
        <el-form-item label="角标文案">
          <el-input v-model="productForm.badgeText" maxlength="50" />
        </el-form-item>
        <el-form-item v-if="productForm.productType === 2" label="库存">
          <el-input-number v-model="productForm.stockValue" :min="0" :precision="0" style="width: 220px" />
        </el-form-item>
        <el-form-item v-if="productForm.productType === 2" label="发货说明">
          <el-input v-model="productForm.deliveryDesc" type="textarea" :rows="2" />
        </el-form-item>
        <el-form-item v-if="productForm.productType === 1" label="服务说明">
          <el-input v-model="productForm.serviceDesc" type="textarea" :rows="2" />
        </el-form-item>
        <el-form-item label="详情文案">
          <el-input v-model="productForm.detailText" type="textarea" :rows="3" />
        </el-form-item>
      </el-form>
      <span slot="footer">
        <el-button @click="productDialogVisible = false">取消</el-button>
        <el-button type="primary" @click="submitProduct">保存</el-button>
      </span>
    </el-dialog>

    <el-dialog :title="categoryForm.id ? '编辑分类' : '新增分类'" :visible.sync="categoryDialogVisible" width="520px">
      <el-form ref="categoryFormRef" :model="categoryForm" :rules="categoryRules" label-width="96px">
        <el-form-item v-if="categoryForm.parentId && categoryForm.parentId !== '0'" label="父级分类">
          <el-input :value="categoryForm.parentName" disabled />
        </el-form-item>
        <el-form-item v-else-if="categoryForm.id && categoryForm.level === 2" label="父级分类">
          <el-select v-model="categoryForm.parentId" placeholder="请选择一级分类" style="width: 100%">
            <el-option
              v-for="item in parentCategoryOptions"
              :key="item.id"
              :label="item.categoryName"
              :value="item.id"
            />
          </el-select>
        </el-form-item>
        <el-form-item label="分类名称" prop="categoryName">
          <el-input v-model="categoryForm.categoryName" maxlength="60" show-word-limit />
        </el-form-item>
        <el-form-item label="排序">
          <el-input-number v-model="categoryForm.sortNo" :min="0" :step="1" style="width: 220px" />
        </el-form-item>
        <el-form-item label="状态">
          <el-radio-group v-model="categoryForm.status">
            <el-radio :label="1">启用</el-radio>
            <el-radio :label="0">停用</el-radio>
          </el-radio-group>
        </el-form-item>
      </el-form>
      <span slot="footer">
        <el-button @click="categoryDialogVisible = false">取消</el-button>
        <el-button type="primary" @click="submitCategory">保存</el-button>
      </span>
    </el-dialog>
  </div>
</template>

<script>
import { MessageBox } from 'element-ui'
import request, { API_BASE_URL } from '../../../utils/request'
import { hasPermission } from '../../../utils/auth'
import {
  createMallCategory,
  createMallProduct,
  deleteMallCategory,
  getMallCategories,
  getMallMerchantTargets,
  getMallProducts,
  getMallRecommendation,
  offlineMallProduct,
  publishMallProduct,
  saveMallRecommendation,
  updateMallCategory,
  updateMallProduct
} from '../../../api/mall'

const productTypeOptions = [
  { label: '虚拟服务', value: 1 },
  { label: '实物商品', value: 2 }
]

const publishSourceOptions = [
  { label: '平台自营', value: 1 },
  { label: '商家代发', value: 2 }
]

const statusOptions = [
  { label: '草稿', value: 1 },
  { label: '已上架', value: 2 },
  { label: '已下架', value: 3 }
]

const imageAccept = 'image/png,image/jpeg,image/jpg,image/webp'
const videoAccept = 'video/mp4,video/quicktime,video/x-m4v,video/3gpp,video/x-msvideo'

function defaultCategoryForm() {
  return {
    id: '',
    parentId: '0',
    parentName: '',
    level: 1,
    categoryName: '',
    sortNo: 0,
    status: 1
  }
}

function defaultProductForm() {
  return {
    id: '',
    productType: 1,
    publishSource: 1,
    merchantUserId: '',
    categoryId: '',
    title: '',
    subtitle: '',
    coverUrl: '',
    imageUrls: '',
    price: 0,
    originalPrice: undefined,
    freightType: 1,
    freightAmount: 0,
    badgeText: '',
    stockValue: 0,
    deliveryDesc: '',
    serviceDesc: '',
    detailText: ''
  }
}

export default {
  name: 'MallCenterPage',
  data() {
    return {
      activeTab: 'products',
      products: [],
      query: {
        keyword: '',
        status: undefined,
        productType: undefined,
        publishSource: undefined,
        categoryId: ''
      },
      productDialogVisible: false,
      productForm: defaultProductForm(),
      productMediaList: [],
      merchantOptions: [],
      merchantLoading: false,
      recommendLoading: false,
      recommendOptions: [],
      recommendProducts: [],
      recommendForm: {
        recommendMode: 2,
        productIds: []
      },
      categoryTree: [],
      categoryQuery: {
        keyword: ''
      },
      categoryDialogVisible: false,
      categoryForm: defaultCategoryForm(),
      categoryRules: {
        categoryName: [{ required: true, message: '请输入分类名称', trigger: 'blur' }]
      },
      productTypeOptions,
      publishSourceOptions,
      statusOptions,
      imageAccept,
      videoAccept,
      categoryCascaderProps: {
        value: 'id',
        label: 'categoryName',
        children: 'children',
        leaf: 'leaf',
        emitPath: false
      },
      productRules: {
        productType: [{ required: true, message: '请选择商品类型', trigger: 'change' }],
        publishSource: [{ required: true, message: '请选择发布来源', trigger: 'change' }],
        categoryId: [{ required: true, message: '请选择商品分类', trigger: 'change' }],
        merchantUserId: [{
          validator: (rule, value, callback) => {
            if (this.productForm.publishSource === 2 && !value) {
              callback(new Error('请选择归属商家'))
              return
            }
            callback()
          },
          trigger: 'change'
        }],
        title: [{ required: true, message: '请输入商品标题', trigger: 'blur' }],
        price: [{ required: true, message: '请输入售价', trigger: 'change' }]
      }
    }
  },
  computed: {
    canCreate() {
      return hasPermission('mall_product:create')
    },
    canUpdate() {
      return hasPermission('mall_product:update')
    },
    canPublish() {
      return hasPermission('mall_product:publish')
    },
    canOffline() {
      return hasPermission('mall_product:offline')
    },
    canRecommend() {
      return hasPermission('mall_product:recommend')
    },
    canCategory() {
      return hasPermission('mall_product:category')
    },
    parentCategoryOptions() {
      return (this.categoryTree || []).filter(item => Number(item.level) === 1)
    },
    enabledCategoryOptions() {
      return (this.categoryTree || [])
        .filter(item => Number(item.status) === 1)
        .map(parent => ({
          id: parent.id,
          categoryName: parent.categoryName,
          children: (parent.children || [])
            .filter(child => Number(child.status) === 1)
            .map(child => ({
              id: child.id,
              categoryName: child.categoryName,
              leaf: true
            }))
        }))
        .filter(parent => (parent.children || []).length > 0)
    }
  },
  created() {
    this.refreshAll()
  },
  methods: {
    async refreshAll() {
      await Promise.all([
        this.fetchProducts(),
        this.fetchRecommendation(),
        this.fetchCategories()
      ])
    },
    async fetchProducts() {
      const params = { ...this.query }
      if (!params.categoryId) {
        delete params.categoryId
      }
      this.products = await getMallProducts(params)
    },
    async fetchRecommendation() {
      const data = await getMallRecommendation()
      this.recommendProducts = Array.isArray(data.products) ? data.products : []
      this.recommendForm = {
        recommendMode: data.recommendMode || 2,
        productIds: this.recommendProducts.map(item => String(item.id))
      }
      this.recommendOptions = this.mergeProductOptions(this.recommendProducts, this.recommendOptions)
    },
    async fetchCategories() {
      const list = await getMallCategories({ ...this.categoryQuery })
      this.categoryTree = Array.isArray(list) ? list : []
    },
    async fetchMerchantOptions(keyword = '') {
      this.merchantLoading = true
      try {
        this.merchantOptions = await getMallMerchantTargets({ keyword })
      } finally {
        this.merchantLoading = false
      }
    },
    async fetchRecommendProductOptions(keyword = '') {
      this.recommendLoading = true
      try {
        const list = await getMallProducts({ keyword, status: 2 })
        this.recommendOptions = this.mergeProductOptions(this.recommendProducts, Array.isArray(list) ? list : [])
      } finally {
        this.recommendLoading = false
      }
    },
    mergeProductOptions(left, right) {
      const map = new Map()
      ;[...(left || []), ...(right || [])].forEach(item => {
        if (item && item.id) {
          map.set(String(item.id), item)
        }
      })
      return Array.from(map.values())
    },
    categoryLabel(row) {
      if (!row) return '-'
      if (row.parentCategoryName && row.categoryName) {
        return `${row.parentCategoryName} / ${row.categoryName}`
      }
      if (row.categoryName) return row.categoryName
      return '未分类'
    },
    handlePublishSourceChange(value) {
      if (value === 1) {
        this.productForm.merchantUserId = ''
      } else {
        this.fetchMerchantOptions('')
      }
    },
    openProductDialog(row) {
      this.productForm = row
        ? {
            ...defaultProductForm(),
            ...row,
            id: row.id ? String(row.id) : '',
            merchantUserId: row.merchantUserId ? String(row.merchantUserId) : '',
            categoryId: row.categoryId ? String(row.categoryId) : ''
          }
        : defaultProductForm()
      this.productMediaList = this.parseProductMedia(this.productForm.imageUrls, this.productForm.coverUrl)
      this.syncCoverFromMedia()
      if (this.productForm.publishSource === 2) {
        this.fetchMerchantOptions('')
      }
      this.productDialogVisible = true
      this.$nextTick(() => {
        if (this.$refs.productFormRef) {
          this.$refs.productFormRef.clearValidate()
        }
      })
    },
    submitProduct() {
      this.$refs.productFormRef.validate(async valid => {
        if (!valid) return
        this.syncCoverFromMedia()
        if (this.productForm.productType !== 2) {
          this.productForm.freightType = 1
          this.productForm.freightAmount = 0
        }
        const payload = {
          ...this.productForm,
          merchantUserId: this.productForm.publishSource === 2 ? this.productForm.merchantUserId : null,
          categoryId: this.productForm.categoryId ? String(this.productForm.categoryId) : '',
          coverUrl: this.productForm.coverUrl || '',
          imageUrls: JSON.stringify(this.productMediaList),
          jumpUrl: ''
        }
        if (this.productForm.id) {
          await updateMallProduct(this.productForm.id, payload)
        } else {
          await createMallProduct(payload)
        }
        this.$message.success('商品保存成功')
        this.productDialogVisible = false
        await this.fetchProducts()
      })
    },
    async uploadProductMedia(option, mediaType) {
      const file = option && option.file
      if (!file) return
      const formData = new FormData()
      formData.append('file', file)
      try {
        const data = await request({
          url: '/api/common/upload',
          method: 'post',
          data: formData,
          timeout: 120000
        })
        if (!data || !data.url) {
          throw new Error('上传失败')
        }
        this.productMediaList.push({
          type: mediaType === 'video' ? 'video' : 'image',
          url: data.url
        })
        this.syncCoverFromMedia()
        this.$message.success('上传成功')
        if (option.onSuccess) option.onSuccess(data)
      } catch (error) {
        this.$message.error(error.message || '上传失败')
        if (option.onError) option.onError(error)
      }
    },
    setCoverMedia(item) {
      if (!item || item.type !== 'image') return
      this.productForm.coverUrl = item.url
    },
    removeProductMedia(index) {
      const removed = this.productMediaList.splice(index, 1)[0]
      if (removed && removed.url === this.productForm.coverUrl) {
        this.productForm.coverUrl = ''
      }
      this.syncCoverFromMedia()
    },
    syncCoverFromMedia() {
      if (this.productForm.coverUrl && this.productMediaList.some(item => item.type === 'image' && item.url === this.productForm.coverUrl)) {
        return
      }
      const firstImage = this.productMediaList.find(item => item.type === 'image' && item.url)
      this.productForm.coverUrl = firstImage ? firstImage.url : ''
    },
    productCoverUrl(row) {
      if (!row) return ''
      if (row.coverUrl) return row.coverUrl
      const firstImage = this.parseProductMedia(row.imageUrls, '').find(item => item.type === 'image' && item.url)
      return firstImage ? firstImage.url : ''
    },
    parseProductMedia(rawValue, coverUrl) {
      const items = []
      const pushItem = (value, type) => {
        const url = typeof value === 'string' ? value.trim() : ''
        if (!url || items.some(item => item.url === url)) return
        items.push({ type: type || this.guessMediaType(url), url })
      }
      if (rawValue) {
        try {
          const parsed = JSON.parse(rawValue)
          if (Array.isArray(parsed)) {
            parsed.forEach(item => {
              if (typeof item === 'string') {
                pushItem(item)
              } else if (item && item.url) {
                pushItem(item.url, item.type)
              }
            })
          }
        } catch (error) {
          String(rawValue).split(',').forEach(url => pushItem(url))
        }
      }
      pushItem(coverUrl, 'image')
      return items
    },
    guessMediaType(url) {
      return /\.(mp4|mov|m4v|3gp|avi)$/i.test(url || '') ? 'video' : 'image'
    },
    async publishProduct(row) {
      await publishMallProduct(row.id)
      this.$message.success('商品已上架')
      await this.refreshAll()
    },
    async offlineProduct(row) {
      try {
        await MessageBox.confirm(`确认下架商品“${row.title}”吗？`, '下架确认', {
          type: 'warning',
          confirmButtonText: '下架',
          cancelButtonText: '取消'
        })
      } catch (e) {
        return
      }
      await offlineMallProduct(row.id)
      this.$message.success('商品已下架')
      await this.refreshAll()
    },
    async submitRecommendation() {
      await saveMallRecommendation({
        recommendMode: this.recommendForm.recommendMode,
        productIds: this.recommendForm.productIds.map(id => Number(id))
      })
      this.$message.success('推荐配置已保存')
      await this.fetchRecommendation()
    },
    openCategoryDialog(row, parentRow) {
      if (row) {
        this.categoryForm = {
          ...defaultCategoryForm(),
          id: String(row.id),
          parentId: row.parentId ? String(row.parentId) : '0',
          parentName: row.parentName || '',
          level: row.level || 1,
          categoryName: row.categoryName || '',
          sortNo: row.sortNo == null ? 0 : row.sortNo,
          status: row.status == null ? 1 : row.status
        }
      } else if (parentRow) {
        this.categoryForm = {
          ...defaultCategoryForm(),
          parentId: String(parentRow.id),
          parentName: parentRow.categoryName || '',
          level: 2
        }
      } else {
        this.categoryForm = defaultCategoryForm()
      }
      this.categoryDialogVisible = true
      this.$nextTick(() => {
        if (this.$refs.categoryFormRef) {
          this.$refs.categoryFormRef.clearValidate()
        }
      })
    },
    submitCategory() {
      this.$refs.categoryFormRef.validate(async valid => {
        if (!valid) return
        const parentId = this.categoryForm.parentId && this.categoryForm.parentId !== '0'
          ? String(this.categoryForm.parentId)
          : 0
        const payload = {
          parentId,
          categoryName: this.categoryForm.categoryName,
          sortNo: this.categoryForm.sortNo,
          status: this.categoryForm.status
        }
        if (this.categoryForm.id) {
          await updateMallCategory(this.categoryForm.id, payload)
        } else {
          await createMallCategory(payload)
        }
        this.$message.success('分类保存成功')
        this.categoryDialogVisible = false
        await this.fetchCategories()
      })
    },
    async removeCategory(row) {
      if (!row || !row.id) return
      const hasChildren = Array.isArray(row.children) && row.children.length > 0
      const message = hasChildren
        ? `分类“${row.categoryName}”下还有子分类，请先删除子分类。`
        : `确认删除分类“${row.categoryName}”吗？`
      if (hasChildren) {
        this.$message.warning(message)
        return
      }
      try {
        await MessageBox.confirm(message, '删除确认', {
          type: 'warning',
          confirmButtonText: '删除',
          cancelButtonText: '取消'
        })
      } catch (e) {
        return
      }
      await deleteMallCategory(row.id)
      this.$message.success('分类已删除')
      await this.fetchCategories()
    },
    productTypeLabel(value) {
      const item = this.productTypeOptions.find(option => option.value === value)
      return item ? item.label : '-'
    },
    publishSourceLabel(value) {
      const item = this.publishSourceOptions.find(option => option.value === value)
      return item ? item.label : '-'
    },
    freightLabel(row) {
      if (!row || Number(row.productType) !== 2) return '-'
      if (Number(row.freightType) === 2) {
        const amount = Number(row.freightAmount)
        return `￥${Number.isFinite(amount) ? amount.toFixed(2) : '0.00'}`
      }
      return '包邮'
    },
    statusLabel(value) {
      const item = this.statusOptions.find(option => option.value === value)
      return item ? item.label : '-'
    },
    statusTagType(value) {
      if (value === 2) return 'success'
      if (value === 3) return 'info'
      return 'warning'
    },
    fullUrl(url) {
      if (!url) return ''
      if (url.startsWith('http')) return url
      if (url.startsWith('/uploads/')) {
        const uploadPath = url.replace('/uploads/', '')
        if (uploadPath.includes('/')) {
          return `${API_BASE_URL}${url}`
        }
        return `${API_BASE_URL}/api/common/file/${uploadPath}`
      }
      return `${API_BASE_URL}${url}`
    }
  }
}
</script>

<style scoped>
.page-head,
.section-header,
.toolbar,
.toolbar-left {
  display: flex;
  align-items: center;
}

.page-head,
.section-header,
.toolbar {
  justify-content: space-between;
}

.toolbar {
  margin-bottom: 16px;
}

.toolbar-left {
  flex-wrap: wrap;
  gap: 10px;
}

.section-card {
  margin-top: 10px;
}

.freight-editor,
.media-upload-actions {
  display: flex;
  align-items: center;
  flex-wrap: wrap;
  gap: 10px;
}

.product-media-grid {
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(150px, 1fr));
  gap: 12px;
  margin-top: 12px;
}

.product-media-card {
  position: relative;
  overflow: hidden;
  min-height: 132px;
  border: 1px solid #ebeef5;
  border-radius: 10px;
  background: #f5f7fa;
}

.product-media-card-cover {
  border-color: #409eff;
  box-shadow: 0 0 0 2px rgba(64, 158, 255, 0.12);
}

.product-media-card img,
.product-media-video {
  display: block;
  width: 100%;
  height: 112px;
  object-fit: cover;
  background: #111827;
}

.media-type-badge,
.cover-badge {
  position: absolute;
  top: 8px;
  padding: 2px 7px;
  border-radius: 999px;
  color: #fff;
  font-size: 12px;
  line-height: 18px;
}

.media-type-badge {
  left: 8px;
  background: rgba(17, 24, 39, 0.72);
}

.cover-badge {
  right: 8px;
  background: rgba(64, 158, 255, 0.9);
}

.product-media-actions {
  display: flex;
  align-items: center;
  justify-content: space-between;
  min-height: 32px;
  padding: 0 8px;
  background: #fff;
}

.product-cell {
  display: flex;
  align-items: center;
  gap: 12px;
}

.product-cover {
  width: 54px;
  height: 54px;
  border-radius: 6px;
  object-fit: cover;
  background: #f5f7fa;
  flex-shrink: 0;
}

.product-cover-empty {
  display: flex;
  align-items: center;
  justify-content: center;
  color: #909399;
  font-size: 12px;
}

.product-info {
  min-width: 0;
}

.product-title {
  font-weight: 600;
  color: #303133;
  line-height: 1.4;
}

.product-subtitle,
.muted-text,
.form-tip {
  margin-top: 4px;
  color: #909399;
  font-size: 12px;
  line-height: 1.5;
}

.danger-link {
  color: #f56c6c;
}
</style>


